import React, { useState, useEffect } from 'react';

const API_HOST = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
const resolveLogoUrl = (logo) => {
  if (!logo) return '';
  if (logo.startsWith('http')) return logo;
  if (!API_HOST) return logo; // relative path fallback
  return `${API_HOST}${logo}`;
};
import { BrowserRouter, Routes, Route, Navigate, NavLink, Outlet, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/index.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { OfficeAuthProvider, useOfficeAuth } from './context/OfficeAuthContext';
import { TransportProviderAuthProvider, useTransportProviderAuth } from './context/TransportProviderAuthContext';
import { PassengerNotificationProvider } from './context/PassengerNotificationContext';
import Sidebar from './components/Sidebar';
import NotificationBell from './components/NotificationBell';
import { PROVIDER_NOTIFICATION_TYPES } from './constants/notificationTypes';
import InstallPrompt from './components/InstallPrompt';
import ManifestSwitcher from './components/ManifestSwitcher';

// Admin pages (company_admin)
import LoginPage                from './pages/LoginPage';
import AdminForgotPasswordPage  from './pages/AdminForgotPasswordPage';
import AdminResetPasswordPage   from './pages/AdminResetPasswordPage';
import AdminConfirmEmailPage    from './pages/AdminConfirmEmailPage';
import DashboardPage       from './pages/DashboardPage';
import FleetPage           from './pages/FleetPage';
import TripsPage           from './pages/TripsPage';
import BookingsPage        from './pages/BookingsPage';
import FinancePage         from './pages/FinancePage';
import StaffPage           from './pages/company/StaffPage';
import CompanyProfilePage  from './pages/company/CompanyProfilePage';
import CompanyObligationsPage from './pages/company/CompanyObligationsPage';
import TripTrackingPage    from './pages/TripTrackingPage';
import PromosPage          from './pages/company/PromosPage';
import CompanyOfficesPage  from './pages/company/CompanyOfficesPage';
import CompanyParcelsPage  from './pages/company/CompanyParcelsPage';

// Super admin pages
import SuperDashboardPage  from './pages/superadmin/SuperDashboardPage';
import CompaniesPage       from './pages/superadmin/CompaniesPage';
import PassengersPage      from './pages/superadmin/PassengersPage';
import AllBookingsPage     from './pages/superadmin/AllBookingsPage';
import CityCodesPage       from './pages/superadmin/CityCodesPage';
import VehicleTypesPage    from './pages/superadmin/VehicleTypesPage';
import SuperSettlementPage from './pages/superadmin/SuperSettlementPage';
import PopularRoutesPage   from './pages/superadmin/PopularRoutesPage';
import SuperProfilePage    from './pages/super/SuperProfilePage';
import OfficesPage         from './pages/superadmin/OfficesPage';
import SuperParcelsPage    from './pages/superadmin/SuperParcelsPage';
import CommissionPage      from './pages/superadmin/CommissionPage';
import SuperTransportProvidersPage from './pages/superadmin/SuperTransportProvidersPage';
import SuperTransportPaymentsPage  from './pages/superadmin/SuperTransportPaymentsPage';

// Office portal pages
import OfficeLoginPage      from './pages/office/OfficeLoginPage';
import OfficeDashboardPage  from './pages/office/OfficeDashboardPage';
import OfficeNewBookingPage from './pages/office/OfficeNewBookingPage';
import OfficeBookingsPage   from './pages/office/OfficeBookingsPage';
import OfficeCommissionPage from './pages/office/OfficeCommissionPage';
import OfficeParcelsPage    from './pages/office/OfficeParcelsPage';
import OfficeNewParcelPage  from './pages/office/OfficeNewParcelPage';

// Transport Provider portal
import TransportProviderLoginPage    from './pages/transport-provider/TransportProviderLoginPage';
import TransportProviderRegisterPage from './pages/transport-provider/TransportProviderRegisterPage';
import TransportProviderStaffPage    from './pages/transport-provider/TransportProviderStaffPage';
import TransportProviderJobBoardPage from './pages/transport-provider/TransportProviderJobBoardPage';
import TransportProviderQuotesPage   from './pages/transport-provider/TransportProviderQuotesPage';
import TransportProviderOrdersPage   from './pages/transport-provider/TransportProviderOrdersPage';
import TransportProviderOrderDetailPage from './pages/transport-provider/TransportProviderOrderDetailPage';
import TransportProviderVehiclesPage from './pages/transport-provider/TransportProviderVehiclesPage';
import RideJobBoardPage        from './pages/transport-provider/RideJobBoardPage';
import RideOrdersPage          from './pages/transport-provider/RideOrdersPage';
import RideProviderOrderDetailPage from './pages/transport-provider/RideOrderDetailPage';
import TransportProviderCommissionPage from './pages/transport-provider/TransportProviderCommissionPage';

// Staff portal (driver / bus_host)
import BoardingPage from './pages/BoardingPage';

// Public
import CompanyRegisterPage   from './pages/CompanyRegisterPage';
import VerifyPage            from './pages/VerifyPage';
import TrackParcelPage       from './pages/TrackParcelPage';
import DeleteAccountInfoPage from './pages/DeleteAccountInfoPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Passenger portal
import PassengerLoginPage    from './pages/passenger/PassengerLoginPage';
import PassengerSearchPage   from './pages/passenger/PassengerSearchPage';
import PassengerBookPage     from './pages/passenger/PassengerBookPage';
import PassengerBookingsPage from './pages/passenger/PassengerBookingsPage';
import PassengerProfilePage  from './pages/passenger/PassengerProfilePage';
import PassengerParcelsPage    from './pages/passenger/PassengerParcelsPage';
import PassengerSendParcelPage from './pages/passenger/PassengerSendParcelPage';
import ForgotPasswordPage    from './pages/passenger/ForgotPasswordPage';
import ResetPasswordPage     from './pages/passenger/ResetPasswordPage';
import TransportRequestsPage    from './pages/passenger/TransportRequestsPage';
import TransportRequestNewPage  from './pages/passenger/TransportRequestNewPage';
import TransportRequestDetailPage from './pages/passenger/TransportRequestDetailPage';
import TransportOrderDetailPage   from './pages/passenger/TransportOrderDetailPage';
import RideRequestsPage      from './pages/passenger/RideRequestsPage';
import RideRequestNewPage    from './pages/passenger/RideRequestNewPage';
import RideRequestDetailPage from './pages/passenger/RideRequestDetailPage';
import RideOrderDetailPage   from './pages/passenger/RideOrderDetailPage';

// ── Shared admin layout (sidebar + main) ────────────────────────────────────
const AdminLayout = () => {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companyLogoError, setCompanyLogoError] = useState(false);

  // reset error state whenever company logo changes
  React.useEffect(() => { setCompanyLogoError(false); }, [user?.company?.logo]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-blue-600 text-xl">جاري التحميل...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;
  if (!['company_admin', 'super_admin', 'driver', 'bus_host'].includes(user.role)) return <Navigate to="/admin/login" replace />;

  const isSuper = user.role === 'super_admin';
  const isStaff = ['driver', 'bus_host'].includes(user.role);

  return (
    <div className="flex min-h-screen">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Desktop top bar */}
        <div
          dir="rtl"
          className={`hidden lg:flex items-center justify-between px-4 py-2 border-b ${isSuper ? 'bg-gray-900 border-gray-700/60' : 'bg-slate-800 border-slate-700/60'}`}
        >
          {/* Right side: company logo + name (company_admin only) */}
          {user?.role === 'company_admin' && user?.company ? (
            <div className="flex items-center gap-2">
              {user.company.logo && !companyLogoError ? (
                <img
                  src={resolveLogoUrl(user.company.logo)}
                  alt={user.company.name}
                  className="w-10 h-10 object-contain rounded-lg bg-white/10 p-0.5"
                  onError={() => setCompanyLogoError(true)}
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {user.company.name?.[0]}
                </div>
              )}
              <span className="text-white text-sm font-semibold">{user.company.name}</span>
            </div>
          ) : (
            <div />
          )}
          {/* Left side: notification bell */}
          <div className="flex items-center gap-3">
            <NotificationBell dark />
          </div>
        </div>

        {/* Mobile top bar */}
        <header
          dir="rtl"
          className={`lg:hidden flex items-center justify-between px-4 py-3 shadow-sm ${isSuper ? 'bg-gray-900' : 'bg-slate-800'}`}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-slate-300 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition"
            aria-label="فتح القائمة"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            {user?.role === 'company_admin' && user?.company ? (
              <>
                {user.company.logo && !companyLogoError ? (
                  <img
                    src={resolveLogoUrl(user.company.logo)}
                    alt={user.company.name}
                    className="w-8 h-8 object-contain rounded-lg bg-white/10 p-0.5"
                    onError={() => setCompanyLogoError(true)}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {user.company.name?.[0]}
                  </div>
                )}
                <span className="text-white font-bold text-sm">{user.company.name}</span>
              </>
            ) : (
              <>
                <img src="/logo.png" alt="سافر" className="w-8 h-8 object-contain rounded-lg" />
                <span className="text-white font-bold text-base">سافر</span>
              </>
            )}
          </div>
          <NotificationBell dark />
        </header>
        <main className="flex-1 overflow-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

// ── Guard: only company_admin may enter ──────────────────────────────────────
const CompanyOnly = () => {
  const { user } = useAuth();
  // M-06: was a blacklist that only excluded super_admin, so driver/bus_host
  // (both allowed into AdminLayout) could browse company-admin pages and
  // trigger their API calls. Whitelist company_admin explicitly, matching
  // StaffOnly's pattern below.
  if (user?.role === 'super_admin') return <Navigate to="/admin/super" replace />;
  if (['driver', 'bus_host'].includes(user?.role)) return <Navigate to="/admin/boarding" replace />;
  if (user?.role !== 'company_admin') return <Navigate to="/admin/login" replace />;
  return <Outlet />;
};

// ── Guard: only super_admin may enter ───────────────────────────────────────
const SuperOnly = () => {
  const { user } = useAuth();
  if (user?.role === 'company_admin') return <Navigate to="/admin" replace />;
  return <Outlet />;
};

// ── Guard: driver / bus_host only ────────────────────────────────────────────
const StaffOnly = () => {
  const { user } = useAuth();
  if (user?.role === 'company_admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'super_admin')   return <Navigate to="/admin/super" replace />;
  return <Outlet />;
};

// ── Redirect /passenger/book/:tripId → /book/:tripId (preserves query string) ─
const PassengerBookLegacyRedirect = () => {
  const { tripId } = useParams();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`/book/${tripId}${qs ? `?${qs}` : ''}`} replace />;
};

// ── Office portal layout + guard ────────────────────────────────────────────
// Mobile header previously had no navigation at all (just office name +
// logout) — a phone/tablet user landing anywhere but the dashboard had no
// way to reach the other pages. Mirrors TransportProviderLayout's hamburger
// + slide-down drawer pattern below, same nav items as the desktop sidebar.
const OfficeLayout = () => {
  const { session, loading, logout } = useOfficeAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-blue-600 text-xl">جاري التحميل...</div>
      </div>
    );
  }
  if (!session) return <Navigate to="/office/login" replace />;

  const handleLogout = () => { logout(); navigate('/office/login'); };

  const navItems = [
    { to: '/office', label: 'الرئيسية', end: true },
    { to: '/office/new-booking', label: 'حجز جديد' },
    { to: '/office/bookings', label: 'الحجوزات' },
    { to: '/office/parcels', label: 'الطرود' },
    { to: '/office/new-parcel', label: 'طرد جديد' },
    { to: '/office/commission', label: 'العمولات' },
  ];

  const navLinkCls = ({ isActive }) =>
    `block px-3 py-2.5 rounded-xl text-sm font-medium transition ${
      isActive ? 'bg-blue-600/20 text-blue-400 border border-blue-500/25' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'
    }`;

  return (
    <div className="flex min-h-screen" dir="rtl">
      {/* Sidebar */}
      <aside className="w-56 bg-[#0D1117] flex flex-col flex-shrink-0 hidden lg:flex">
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          <img src="/logo.png" alt="سافر" className="w-10 h-10 object-contain rounded-xl" />
          <div>
            <p className="text-white text-xs font-bold">بوابة المكتب</p>
            <p className="text-blue-400 text-xs truncate max-w-28">{session.office?.name}</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map(({ to, label, end }) => (
            <NavLink key={to} to={to} end={end} className={navLinkCls}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <p className="text-white text-xs font-medium truncate">{session.user?.name}</p>
          <p className="text-slate-500 text-xs mb-3">{session.officeRole === 'admin' ? 'مدير المكتب' : 'مشرف'}</p>
          <button onClick={handleLogout} className="w-full text-xs text-red-400 hover:text-red-300 py-1.5 rounded-lg border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition">
            خروج
          </button>
        </div>
      </aside>

      {/* Mobile header — hamburger opens a slide-down drawer with the same
          nav links the desktop sidebar has. */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#0D1117] border-b border-white/10 flex items-center justify-between px-4 py-3 gap-3">
        <button
          onClick={() => setMobileNavOpen((o) => !o)}
          className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg text-white/80 hover:bg-white/10 transition"
          aria-label="القائمة"
        >
          {mobileNavOpen ? (
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
        <span className="text-white text-sm font-bold truncate flex-1 text-center">{session.office?.name}</span>
        <button onClick={handleLogout} className="flex-shrink-0 text-xs text-red-400 border border-red-500/20 px-3 py-2 rounded-lg">خروج</button>
      </div>

      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-20 top-[57px]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <nav className="relative bg-[#0D1117] border-b border-white/10 px-3 py-3 space-y-0.5 shadow-[0_12px_30px_rgba(0,0,0,.4)] max-h-[calc(100vh-57px)] overflow-y-auto">
            {navItems.map(({ to, label, end }) => (
              <NavLink key={to} to={to} end={end} className={navLinkCls} onClick={() => setMobileNavOpen(false)}>
                {label}
              </NavLink>
            ))}
            <div className="pt-2 mt-2 border-t border-white/10 px-3">
              <p className="text-white text-xs font-medium truncate">{session.user?.name}</p>
              <p className="text-slate-500 text-xs">{session.officeRole === 'admin' ? 'مدير المكتب' : 'مشرف'}</p>
            </div>
          </nav>
        </div>
      )}

      <main className="flex-1 overflow-auto bg-[#FAFAF8] lg:mt-0 mt-12">
        <Outlet />
      </main>
    </div>
  );
};

// ── Transport Provider portal layout + guard — mirrors OfficeLayout exactly,
// one org type over ──────────────────────────────────────────────────────────
const TransportProviderLayout = () => {
  const { session, loading, logout } = useTransportProviderAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Closing on route change — the mobile drawer would otherwise stay open
  // over the next page after a link click (NavLink navigates but doesn't
  // know to close a drawer it's not aware of).
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-blue-600 text-xl">جاري التحميل...</div>
      </div>
    );
  }
  if (!session) return <Navigate to="/transport-provider/login" replace />;

  const handleLogout = () => { logout(); navigate('/transport-provider/login'); };

  // Built once, rendered in both the desktop sidebar and the mobile drawer —
  // same fail-open service-gating logic either way.
  const navItems = [
    ...(session.provider?.servicesCargo !== false ? [
      { to: '/transport-provider', label: 'طلبات النقل', end: true },
      { to: '/transport-provider/quotes', label: 'عروضي' },
      { to: '/transport-provider/orders', label: 'طلباتي' },
    ] : []),
    { to: '/transport-provider/vehicles', label: 'الأسطول' },
    ...(session.provider?.servicesRides !== false ? [
      { to: '/transport-provider/rides', label: 'طلبات المشاوير' },
      { to: '/transport-provider/rides/orders', label: 'مشاويري' },
    ] : []),
    { to: '/transport-provider/commission', label: 'عمولاتي' },
    ...(session.providerRole === 'owner' ? [{ to: '/transport-provider/staff', label: 'فريق العمل' }] : []),
  ];

  const navLinkCls = ({ isActive }) =>
    `block px-3 py-2.5 rounded-xl text-sm font-medium transition ${
      isActive ? 'bg-blue-600/20 text-blue-400 border border-blue-500/25' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'
    }`;

  return (
    <div className="flex min-h-screen" dir="rtl">
      <aside className="w-56 bg-[#0D1117] flex flex-col flex-shrink-0 hidden lg:flex">
        <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo.png" alt="سافر" className="w-10 h-10 object-contain rounded-xl flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-white text-xs font-bold">بوابة مزود النقل</p>
              <p className="text-blue-400 text-xs truncate max-w-28">{session.provider?.name}</p>
            </div>
          </div>
          <NotificationBell dark types={PROVIDER_NOTIFICATION_TYPES} />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map(({ to, label, end }) => (
            <NavLink key={to} to={to} end={end} className={navLinkCls}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <p className="text-white text-xs font-medium truncate">{session.user?.name}</p>
          <p className="text-slate-500 text-xs mb-3">{ROLE_LABEL_AR[session.providerRole] || session.providerRole}</p>
          <button onClick={handleLogout} className="w-full text-xs text-red-400 hover:text-red-300 py-1.5 rounded-lg border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition">
            خروج
          </button>
        </div>
      </aside>

      {/* Mobile header — hamburger opens a slide-down drawer with the same
          nav links the desktop sidebar has; previously this bar only had
          the provider name + logout with no way to navigate at all. */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#0D1117] border-b border-white/10 flex items-center justify-between px-4 py-3 gap-3">
        <button
          onClick={() => setMobileNavOpen((o) => !o)}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg text-white/80 hover:bg-white/10 transition"
          aria-label="القائمة"
        >
          {mobileNavOpen ? (
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
        <span className="text-white text-sm font-bold truncate flex-1 text-center">{session.provider?.name}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          <NotificationBell dark types={PROVIDER_NOTIFICATION_TYPES} />
          <button onClick={handleLogout} className="text-xs text-red-400 border border-red-500/20 px-3 py-1.5 rounded-lg">خروج</button>
        </div>
      </div>

      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-20 top-[57px]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <nav className="relative bg-[#0D1117] border-b border-white/10 px-3 py-3 space-y-0.5 shadow-[0_12px_30px_rgba(0,0,0,.4)] max-h-[calc(100vh-57px)] overflow-y-auto">
            {navItems.map(({ to, label, end }) => (
              <NavLink key={to} to={to} end={end} className={navLinkCls} onClick={() => setMobileNavOpen(false)}>
                {label}
              </NavLink>
            ))}
            <div className="pt-2 mt-2 border-t border-white/10 px-3">
              <p className="text-white text-xs font-medium truncate">{session.user?.name}</p>
              <p className="text-slate-500 text-xs">{ROLE_LABEL_AR[session.providerRole] || session.providerRole}</p>
            </div>
          </nav>
        </div>
      )}

      <main className="flex-1 overflow-auto bg-[#FAFAF8] lg:mt-0 mt-12">
        <Outlet />
      </main>
    </div>
  );
};
const ROLE_LABEL_AR = { owner: 'مالك', dispatcher: 'منسّق', driver: 'سائق' };

// ── Passenger portal root gate ──────────────────────────────────────────────
// A guest visiting / sees the login screen (phone + PIN) first, not the
// search page directly — PassengerLoginPage's "متابعة كضيف" button is how
// they reach PassengerSearchPage/guest bus booking from there. Logged-in
// passengers skip straight to the search page as before.
const PassengerHomeGate = ({ homeResetKey }) => {
  const isLoggedIn = !!JSON.parse(localStorage.getItem('passenger_user') || 'null')?.id;
  // Set by PassengerLoginPage's "متابعة كضيف" button — sessionStorage (not
  // localStorage) so a fresh visit/new tab shows login again as required,
  // but in-app navigation back to "/" during this same guest session (logo
  // click, a cancelled booking, etc.) doesn't bounce them back to login.
  const guestContinued = sessionStorage.getItem('safer_guest_continue') === '1';
  return isLoggedIn || guestContinued
    ? <PassengerSearchPage key={homeResetKey} />
    : <Navigate to="/login" replace />;
};

// ── Login smart redirect ─────────────────────────────────────────────────────
const LoginRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role === 'super_admin')                        return <Navigate to="/admin/super" replace />;
  if (user?.role === 'company_admin')                      return <Navigate to="/admin" replace />;
  if (['driver', 'bus_host'].includes(user?.role))         return <Navigate to="/admin/boarding" replace />;
  return <LoginPage />;
};

function App() {
  // goHome() dispatches this instead of a hard reload when already on '/'
  // (a hard window.location.href reload would kick an installed PWA out of
  // standalone mode, showing the browser address bar). Bumping this key
  // forces PassengerSearchPage to unmount/remount — a real reset of all its
  // local view state (selected service tab, search results, etc.) — with
  // zero navigation involved, so standalone mode is never broken.
  const [homeResetKey, setHomeResetKey] = useState(0);
  useEffect(() => {
    const bump = () => setHomeResetKey((k) => k + 1);
    window.addEventListener('safer:go-home', bump);
    return () => window.removeEventListener('safer:go-home', bump);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Wraps Routes (not just the passenger pages) so it stays mounted
            across every navigation instead of reconnecting its socket on
            each route change — see PassengerNotificationContext for how it
            no-ops when no passenger is logged in. */}
        <PassengerNotificationProvider>
        <Routes>
          {/* ── Public ── */}
          <Route path="/company-register"      element={<CompanyRegisterPage />} />
          <Route path="/verify/:bookingNumber" element={<VerifyPage />} />
          <Route path="/track-parcel/:trackingNumber" element={<TrackParcelPage />} />
          <Route path="/delete-account" element={<DeleteAccountInfoPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

          {/* ── Passenger portal (root) ── */}
          <Route path="/"                 element={<PassengerHomeGate homeResetKey={homeResetKey} />} />
          <Route path="/login"            element={<PassengerLoginPage />} />
          <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
          <Route path="/reset-password"   element={<ResetPasswordPage />} />
          <Route path="/profile"          element={<PassengerProfilePage />} />
          <Route path="/book/:tripId"     element={<PassengerBookPage />} />
          <Route path="/bookings"         element={<PassengerBookingsPage />} />
          <Route path="/parcels"          element={<PassengerParcelsPage />} />
          <Route path="/parcels/new"      element={<PassengerSendParcelPage />} />
          <Route path="/transport"                  element={<TransportRequestsPage />} />
          <Route path="/transport/new"              element={<TransportRequestNewPage />} />
          <Route path="/transport/requests/:id"     element={<TransportRequestDetailPage />} />
          <Route path="/transport/orders/:id"       element={<TransportOrderDetailPage />} />
          <Route path="/rides"                      element={<RideRequestsPage />} />
          <Route path="/rides/new"                  element={<RideRequestNewPage />} />
          <Route path="/rides/requests/:id"         element={<RideRequestDetailPage />} />
          <Route path="/rides/orders/:id"           element={<RideOrderDetailPage />} />

          {/* Legacy /passenger/* redirects — preserve old bookmarks/links */}
          <Route path="/passenger"                  element={<Navigate to="/" replace />} />
          <Route path="/passenger/search"           element={<Navigate to="/" replace />} />
          <Route path="/passenger/login"            element={<Navigate to="/login" replace />} />
          <Route path="/passenger/bookings"         element={<Navigate to="/bookings" replace />} />
          <Route path="/passenger/book/:tripId/*"   element={<PassengerBookLegacyRedirect />} />

          {/* ── Admin portal (/admin prefix) ── */}
          <Route path="/admin/login"            element={<LoginRoute />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
          <Route path="/admin/reset-password"  element={<AdminResetPasswordPage />} />
          <Route path="/admin/confirm-email"   element={<AdminConfirmEmailPage />} />

          <Route path="/admin" element={<AdminLayout />}>

            {/* Company admin routes */}
            <Route element={<CompanyOnly />}>
              <Route index                         element={<DashboardPage />} />
              <Route path="fleet"                  element={<FleetPage />} />
              <Route path="trips"                  element={<TripsPage />} />
              <Route path="bookings"               element={<BookingsPage />} />
              <Route path="finance"                element={<FinancePage />} />
              <Route path="staff"                  element={<StaffPage />} />
              <Route path="obligations"            element={<CompanyObligationsPage />} />
              <Route path="profile"                element={<CompanyProfilePage />} />
              <Route path="tracking"               element={<TripTrackingPage />} />
              <Route path="promos"                 element={<PromosPage />} />
              <Route path="offices"                element={<CompanyOfficesPage />} />
              <Route path="parcels"                element={<CompanyParcelsPage />} />
            </Route>

            {/* Super admin routes */}
            <Route element={<SuperOnly />}>
              <Route path="super"                  element={<SuperDashboardPage />} />
              <Route path="super/companies"        element={<CompaniesPage />} />
              <Route path="super/passengers"       element={<PassengersPage />} />
              <Route path="super/bookings"         element={<AllBookingsPage />} />
              <Route path="super/settlements"      element={<SuperSettlementPage />} />
              <Route path="super/offices"          element={<OfficesPage />} />
              <Route path="super/parcels"          element={<SuperParcelsPage />} />
              <Route path="super/transport-providers" element={<SuperTransportProvidersPage />} />
              <Route path="super/transport-payments"  element={<SuperTransportPaymentsPage />} />
              <Route path="super/commission"       element={<CommissionPage />} />
              <Route path="super/city-codes"       element={<CityCodesPage />} />
              <Route path="super/vehicle-types"    element={<VehicleTypesPage />} />
              <Route path="super/popular-routes"   element={<PopularRoutesPage />} />
              <Route path="super/profile"          element={<SuperProfilePage />} />
            </Route>

            {/* Driver / Bus host routes */}
            <Route element={<StaffOnly />}>
              <Route path="boarding"               element={<BoardingPage />} />
            </Route>

          </Route>

          {/* ── Office portal (/office prefix) ── */}
          <Route path="/office" element={<OfficeAuthProvider><Outlet /></OfficeAuthProvider>}>
            <Route path="login" element={<OfficeLoginPage />} />
            <Route element={<OfficeLayout />}>
              <Route index                element={<OfficeDashboardPage />} />
              <Route path="new-booking"   element={<OfficeNewBookingPage />} />
              <Route path="bookings"      element={<OfficeBookingsPage />} />
              <Route path="parcels"       element={<OfficeParcelsPage />} />
              <Route path="new-parcel"    element={<OfficeNewParcelPage />} />
              <Route path="commission"    element={<OfficeCommissionPage />} />
            </Route>
          </Route>

          {/* ── Transport Provider portal (/transport-provider prefix) ── */}
          <Route path="/transport-provider" element={<TransportProviderAuthProvider><Outlet /></TransportProviderAuthProvider>}>
            <Route path="login"    element={<TransportProviderLoginPage />} />
            <Route path="register" element={<TransportProviderRegisterPage />} />
            <Route element={<TransportProviderLayout />}>
              <Route index               element={<TransportProviderJobBoardPage />} />
              <Route path="quotes"       element={<TransportProviderQuotesPage />} />
              <Route path="orders"       element={<TransportProviderOrdersPage />} />
              <Route path="orders/:id"   element={<TransportProviderOrderDetailPage />} />
              <Route path="vehicles"     element={<TransportProviderVehiclesPage />} />
              <Route path="staff"        element={<TransportProviderStaffPage />} />
              <Route path="rides"                element={<RideJobBoardPage />} />
              <Route path="rides/orders"         element={<RideOrdersPage />} />
              <Route path="rides/orders/:id"     element={<RideProviderOrderDetailPage />} />
              <Route path="commission"           element={<TransportProviderCommissionPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <ManifestSwitcher />
        <InstallPrompt />
        </PassengerNotificationProvider>
      </BrowserRouter>

      <ToastContainer position="top-right" autoClose={4000} newestOnTop closeOnClick draggable />
    </AuthProvider>
  );
}

export default App;
